
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Table, TableContainer,
  TableHead, TableBody, TableRow, TableCell, Paper, IconButton,
  CircularProgress, Snackbar, Alert
} from '@mui/material';
import { Edit, QrCode } from '@mui/icons-material';
import { QRCodeCanvas } from 'qrcode.react';
import axios from 'axios';
import { API_BASE } from '../config';
import MachineSelectDropdown from '../components/MachineSelectDropdown';
import { ResetConfirmationDialog } from '../components/ResetConfirmationDialog';

const ITEM_API = uid => `${API_BASE}/zts/app/v1.0/list/product/${uid}/`;
const UPDATE_API = id => `${API_BASE}/zts/admin/v1.0/item/${id}/`;
const DISPENSE_API = id => `${API_BASE}/zts/admin/v1.0/dispense/item/${id}/`;

const ItemInventory = () => {
  const [selectedMachine, setSelectedMachine] = useState('');
  const [items, setItems] = useState([]);
  const [locationName, setLocationName] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmResetItem, setConfirmResetItem] = useState(null);
  const [confirmResetAll, setConfirmResetAll] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrExpiry, setQrExpiry] = useState(null);
  const [qrCountdown, setQrCountdown] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  useEffect(() => {
    if (selectedMachine) fetchItems(selectedMachine);
  }, [selectedMachine]);

  useEffect(() => {
    if (!qrExpiry) return;
    const interval = setInterval(() => {
      const secondsLeft = Math.max(0, Math.floor((new Date(qrExpiry).getTime() - Date.now()) / 1000));
      setQrCountdown(secondsLeft);
    }, 1000);
    return () => clearInterval(interval);
  }, [qrExpiry]);

  const fetchItems = async (uid) => {
    setLoading(true);
    setError(false);
    const timeout = setTimeout(() => {
      setLoading(false);
      setError(true);
    }, 45000);

    try {
      const res = await axios.get(ITEM_API(uid));
      clearTimeout(timeout);
      setItems(res.data.product || []);
      setLocationName(res.data.location || '');
      setLoading(false);
      if ((res.data.product || []).length === 0) {
        setSnackbar({ open: true, message: 'No products found for this machine.', severity: 'info' });
      }
    } catch (err) {
      clearTimeout(timeout);
      setError(true);
      setLoading(false);
      setSnackbar({ open: true, message: 'Failed to load data.', severity: 'error' });
    }
  };

  const handleOpen = (item) => {
    setEditing(item);
    setForm({
      id: item.id,
      item_name: item.item_name,
      max_quantity: item.max_quantity,
      balance: item.balance,
      price: item.price,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setForm({});
    setEditing(null);
    setOpen(false);
  };

  const handleReset = () => {
    setForm(prev => ({ ...prev, balance: prev.max_quantity }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      await axios.put(UPDATE_API(form.id), {
        balance_quantity: form.balance,
        sales_price: form.price,
      });
      setSnackbar({ open: true, message: 'Item updated successfully!', severity: 'success' });
      fetchItems(selectedMachine);
      handleClose();
    } catch (err) {
      setSnackbar({ open: true, message: 'Update failed. Please try again.', severity: 'error' });
    }
  };

  const handleResetItem = async () => {
    if (!confirmResetItem) return;
    try {
      await axios.put(UPDATE_API(confirmResetItem.id), {
        balance_quantity: confirmResetItem.max_quantity,
        sales_price: confirmResetItem.price,
      });
      setSnackbar({ open: true, message: `Reset ${confirmResetItem.item_name} to max successfully.`, severity: 'success' });
      fetchItems(selectedMachine);
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to reset ${confirmResetItem.item_name}.`, severity: 'error' });
    }
    setConfirmResetItem(null);
  };

  const handleResetAll = async () => {
    try {
      await Promise.all(items.map(item =>
        axios.put(UPDATE_API(item.id), {
          balance_quantity: item.max_quantity,
          sales_price: item.price,
        })
      ));
      setSnackbar({ open: true, message: 'All items reset to max successfully.', severity: 'success' });
      fetchItems(selectedMachine);
    } catch (err) {
      setSnackbar({ open: true, message: 'Reset all failed.', severity: 'error' });
    }
    setConfirmResetAll(false);
  };

  const handleGenerateQr = async (itemId) => {
    try {
      const res = await axios.post(DISPENSE_API(itemId));
      if (res.data.success) {
        setQrData(res.data.data);
        setQrExpiry(res.data.expiry);
        setQrDialogOpen(true);
      } else {
        setSnackbar({ open: true, message: 'Failed to generate QR.', severity: 'error' });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error generating QR.', severity: 'error' });
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Item Inventory</Typography>

      <MachineSelectDropdown
        selectedMachine={selectedMachine}
        setSelectedMachine={setSelectedMachine}
        setLocationName={setLocationName}
      />

      {items.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button variant="outlined" color="warning" onClick={() => setConfirmResetAll(true)}>
            RESET ALL
          </Button>
        </Box>
      )}

      {loading && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 1 }}>Loading...</Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography color="error" variant="h6">ERROR. CONTENT CANNOT BE LOADED AT THIS MOMENT.</Typography>
          <Button onClick={() => fetchItems(selectedMachine)} variant="outlined" sx={{ mt: 2 }}>Retry</Button>
        </Box>
      )}

      {!loading && !error && items.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Image</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Max Quantity</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Price (RM)</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => (
                <TableRow key={item.id}>
                  <TableCell>
                    <img src={item.item_image_url} alt={item.item_name} style={{ width: 50, height: 50, objectFit: 'cover' }} />
                  </TableCell>
                  <TableCell>{item.item_name}</TableCell>
                  <TableCell>{item.max_quantity}</TableCell>
                  <TableCell>{item.balance}</TableCell>
                  <TableCell>{item.price}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpen(item)}><Edit /></IconButton>
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      sx={{ ml: 1 }}
                      onClick={() => setConfirmResetItem(item)}
                      disabled={item.balance === item.max_quantity}
                    >
                      RESET
                    </Button>
                    <IconButton onClick={() => handleGenerateQr(item.id)} sx={{ ml: 1 }}>
                      <QrCode />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Edit Inventory</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Item" value={form.item_name} fullWidth disabled />
          <TextField margin="dense" label="Max Quantity" value={form.max_quantity} fullWidth disabled />
          <TextField margin="dense" name="balance" label="Balance Quantity" value={form.balance} fullWidth onChange={handleChange} />
          <TextField margin="dense" name="price" label="Sales Price" value={form.price} fullWidth onChange={handleChange} />
          <Button onClick={handleReset} sx={{ mt: 2 }} variant="outlined" color="warning">
            RESET (Set Balance = Max)
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave}>Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)}>
        <DialogTitle>QR Code for Dispense</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          {qrData ? (
            <>
              <QRCodeCanvas value={qrData} size={200} />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Expires at: <strong>{new Date(qrExpiry).toLocaleString()}</strong>
              </Typography>
              <Typography variant="body2">
                Countdown: <strong>{qrCountdown === 0 ? 'Expired' : `${qrCountdown} seconds`}</strong>
              </Typography>
            </>
          ) : (
            <Typography>Generating QR...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ResetConfirmationDialog
        open={!!confirmResetItem}
        onClose={() => setConfirmResetItem(null)}
        onConfirm={handleResetItem}
        content={`Are you sure you want to reset ${confirmResetItem?.item_name} to its max quantity?`}
      />

      <ResetConfirmationDialog
        open={confirmResetAll}
        onClose={() => setConfirmResetAll(false)}
        onConfirm={handleResetAll}
        title="Confirm Reset All"
        content="This will reset all items to their max quantity. Are you sure?"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ItemInventory;
